import type { DateString, DateStringWithTime } from '@/types/common';
import type {
  FormSortConciergeHealthcareFamiliarity,
  FormSortHealthcarePriority,
  FormSortLocation,
} from '@/types/forms';

export type SubmissionUrl = string;
export type WithSubmissionUrl = {
  submissionUrl: SubmissionUrl;
};

export const SUBMISSION_URL_KEY = 'submissionUrl';

export type SalesforceRelationship =
  | 'Spouse'
  | 'Partner'
  | 'Daughter'
  | 'Son'
  | 'Assistant'
  | 'Boyfriend'
  | 'Girlfriend'
  | 'Parent'
  | 'Son-in-law'
  | 'Daughter-in-law'
  | 'Other-in-Law'
  | 'Grandparent'
  | 'Grandson'
  | 'Granddaughter'
  | 'Sibling'
  | 'Staff'
  | 'Ex-Spouse'
  | 'Niece'
  | 'Nephew'
  | 'Stepson'
  | 'Stepdaughter'
  | 'Nanny'
  | 'Other';

export type SalesforceRelationshipKey =
  | 'spouse'
  | 'partner'
  | 'daughter'
  | 'son'
  | 'assistant'
  | 'boyfriend'
  | 'girlfriend'
  | 'parent'
  | 'sonInLaw'
  | 'daughterInLaw'
  | 'otherInLaw'
  | 'grandparent'
  | 'grandson'
  | 'granddaughter'
  | 'sibling'
  | 'staff'
  | 'exSpouse'
  | 'niece'
  | 'nephew'
  | 'stepson'
  | 'stepdaughter'
  | 'nanny'
  | 'other';

export type SalesforcePrimaryRelationship = 'Primary';

// Salesforce Lead Creation Types
export type SalesforceLeadRequest = {
  email?: string;
  zipCode?: string | number;
  submissionURL?: string;
  center?: string;
  utmCode?: string;
  leadSource?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  promotion?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_medium?: string;
  utm_term?: string;
  utm_source?: string;
  ours_device_id?: string;
  vwo_uuid?: string;
  gclid?: string;
  msclkid?: string;
  fbclid?: string;
};

export type SalesforceLeadResponse = {
  leadID?: string;
  error?: string;
};

export type SalesforceProduct = 'Signature' | 'Under-45' | 'Over 60';

export type SalesforcePlan = 'Signature' | 'Under 45' | 'Over 60';

export type SalesforcePlanPricingRequestIndividual = {
  id: string;
  birthDate: DateStringWithTime;
  age: number;
  isPrimary: boolean;
  hasPediatrics: boolean;
};

export type SalesforcePlanPricingRequest = {
  planMembers: SalesforcePlanPricingRequestIndividual[];
};

export type SalesforcePlanPricingResponse = {
  id: string;
  signaturePricing: number;
  platinumPricing: number;
  pediatricsPricing?: number;
};

export type SalesforceCalculatePriceRequestIndividual = {
  name: string;
  relationship: SalesforcePrimaryRelationship | SalesforceRelationship;
  dateOfBirth: DateStringWithTime;
  isPrimary: boolean;
  hasPediatrics: boolean;
  plan?: 'Signature' | 'Platinum' | 'Pediatrics';
};

export type SalesforceCalculatePriceRequest = {
  product?: SalesforceProduct;
  individuals: SalesforceCalculatePriceRequestIndividual[];
  promoCode?: string;
};

export type SalesforceCalculatePriceResponseIndividual = {
  price: number;
  name: string;
  relationship: SalesforcePrimaryRelationship | SalesforceRelationship;
  dateOfBirth: DateStringWithTime;
  isPrimary: boolean;
};

type SalesforcePromotion = {
  id: string;
  ownerId: string;
  isDeleted: boolean;
  name: string;
  promoCode: string;
  percentageReduction: number; // e.g. 10 for 10%
  createdDate: string; // e.g "Sep 12 2025  2:05PM",
  createdById: string;
  lastModifiedDate: string; // As above
  lastModifiedById: string;
  systemModstamp: string; // As above
};

export type SalesforceCalculatePriceResponse = {
  overallPrice: number;
  discountedPrice?: number;
  individualPrices: SalesforceCalculatePriceResponseIndividual[];
  promotionUsed?: SalesforcePromotion;
};

export type SalesforceAddress = {
  country?: string;
  zipCode?: string;
  state?: string;
  city?: string;
  street?: string;
};

export type SalesforceLegalGuardian = {
  firstName: string;
  lastName: string;
  phone: string;
};

export type SalesforceDesignatedAdult = {
  firstName: string;
  lastName: string;
  relationship: string;
  phone: string;
};

export type SalesforceMember = {
  id?: string;
  plan?: string;
  firstName: string;
  lastName: string;
  dob?: DateString;
  sex?: string;
  relationship?: string;
  email?: string;
  phone?: string;
  address?: SalesforceAddress;
  physicianName?: string;
  pediatricianName?: string;
  allergies?: string;
  medicalHistory?: string;
  legalGuardian?: SalesforceLegalGuardian;
  designatedAdult?: SalesforceDesignatedAdult;
  hasPediatrics?: boolean;
};

export type SalesforceLeadDetailsRequest = {
  leadID: string;
  leadSource?: string;
  plan?: string;
  firstName: string;
  lastName: string;
  dob?: DateString;
  zipCode?: string;
  sex?: string;
  email: string;
  phone: string;
  dupeLeadOwnerId?: string;
  dupeContactOwnerId?: string;
  dupeAccountOwnerId?: string;
  address?: SalesforceAddress;
  secondaryAddress?: SalesforceAddress;
  physicianName?: string;
  members?: SalesforceMember[];
  promotion?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_medium?: string;
  utm_term?: string;
  utm_source?: string;
} & SalesforceAdditionalQuestionsMapping;

export type SalesforceAdditionalQuestionsMapping = {
  concierge_healthcare_familiarity?: FormSortConciergeHealthcareFamiliarity;
  healthcare_priorities?: FormSortHealthcarePriority[];
  visited_locations?: FormSortLocation[];
};

export type SalesforceFinalizeLeadResponse = {
  opportunityId: string;
  contactId: string;
  error?: string;
};

export type SalesforceCheckoutRequest = {
  contactID: string;
  reference?: string;
  reference2?: string;
};

export type SalesforceCheckoutResponse = {
  opportunityId: string;
  contactId: string;
  redirectUrl: string;
  redirectCheckoutUrl: string;
  error?: string;
};

export type ContactFormRequest = {
  form_type: 'contact-us' | 'doctors' | 'companies';
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  department?: string;
  job_title?: string;
  company?: string;
  postal_code: string;
  ppc_location: string;
  source_details?: string;
  source_details_other?: string;
  note?: string;
  marketing_consent: boolean;
  email_opt_in: boolean; // alias of marketing_consent
  MembershipOfInterest?: string;
  ContactUsType?: 'General' | 'Pediatrics';
  sms_opt_in?: boolean;
} & SalesforceTrackingParams &
  SalesforceUTMParams;

export type SalesforceContactFormBasePayload = {
  first_name: string;
  last_name: string;
  email: string;
  age?: string;
  status?: string;
  type: string;
  lead_channel: 'Website Form';
  opportunity_type: 'New Membership';
  company: string;
  source_details: string;
  ppc_location: string;
  lead_source?: string;
  email_opt_in?: boolean;
  membership_type: string;
  postal_code: string;
  note?: string;
  membershipOfInterest?: string;
  sms_opt_int?: boolean;
} & (
  | { phone: string; mobile_phone: never }
  | { phone: never; mobile_phone: string }
) &
  SalesforceTrackingParams &
  SalesforceUTMParams;

export type SalesforceContactFormB2CPayload =
  SalesforceContactFormBasePayload & {
    type: 'Individual/Family Member';
    source_details: 'Web Lead';
    membership_type: '';
  };

export type SalesforceContactFormB2MPayload =
  SalesforceContactFormBasePayload & {
    type: 'Medical Partner';
    department: string;
    source_details: 'B2M';
    membership_type: 'Other';
  };

export type SalesforceContactFormB2BPayload =
  SalesforceContactFormBasePayload & {
    type: 'Corporate Member';
    job_title: string;
    source_details: 'B2B';
    membership_type: 'Corporate Standard';
  };

export type SalesforceContactFormPayload =
  | SalesforceContactFormB2CPayload
  | SalesforceContactFormB2MPayload
  | SalesforceContactFormB2BPayload;

export type SalesforceTrackingParams = {
  submission_url?: string;
  gclid?: string;
  ours_device_id?: string;
  vwo_uuid?: string;
  msclkid?: string;
  fbclid?: string;
};

export type SalesforceUTMParams = {
  utmCode?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_term?: string;
  utm_campaign?: string;
  utm_content?: string;
};

export type SalesforceLocationResponse = {
  locationName: string;
  marketName: string;
  salesforceName: string;
  hasPediatrics: boolean;
  alternativeLocations: string[];
};

export type SalesforceLocationErrorResponse = {
  type: string;
  title: string;
  status: number;
  traceId: string;
};

export interface AccountMember {
  contact_id: string;
  first_name: string;
  last_name: string;
  date_of_birth: string;
}

export interface PartialMember {
  contact_id: string;
  first_name: string;
  last_name: string;
  date_of_birth: DateString;
  // Primaries have these filled out already
  email?: string;
  mobile_phone?: string;
  zip_code?: string;
}

export interface FinalizeAccountDetailsResponse {
  plan_name: string | null;
  members: PartialMember[] | null;
}

// Keep this for backward compatibility and error handling
export interface AccountDetailsResponse {
  plan_name?: string | null;
  members?: PartialMember[] | null;
  error?: string;
}

export interface FinalizedMember {
  contact_id: string;
  mailing_street?: string;
  mailing_city?: string;
  mailing_state?: string;
  mobile_phone: string;
  email: string;
  relationship: string;
  gender: string;
  pcp: string;
}

export interface PostAccountDetailsRequest {
  contact_id: string;
  members: FinalizedMember[];
}

export interface PostAccountDetailsResponse {
  success?: boolean;
  error?: string;
}

export interface SalesforceCreateMqlRequest {
  email: string;
  didConsentToMarketing: boolean;
}

export type SalesforceInvoiceRequest = {
  invoiceId: string;
};

export type SalesforceInvoiceFullResponse = {
  attributes: {
    type: string;
    url: string;
  };
  Id: string;
  OwnerId: string;
  IsDeleted: boolean;
  Name: string;
  RecordTypeId: string;
  CreatedDate: string;
  CreatedById: string;
  LastModifiedDate: string;
  LastModifiedById: string;
  SystemModstamp: string;
  LastActivityDate: string | null;
  LastViewedDate: string | null;
  LastReferencedDate: string | null;
  fw1__Account_Name__c: string;
  fw1__Account__c: string;
  fw1__Age__c: number;
  fw1__Alternate_Invoice_Number__c: string;
  fw1__Authorized_Amount__c: number | null;
  fw1__Auto_BillPay__c: boolean;
  fw1__Balance_Due_In_Words__c: string;
  fw1__Balance_Due__c: number;
  fw1__Billing_City__c: string | null;
  fw1__Billing_Country__c: string;
  fw1__Billing_Period_End__c: string;
  fw1__Billing_Period_Start__c: string;
  fw1__Billing_Period__c: string | null;
  fw1__Billing_State__c: string | null;
  fw1__Billing_Street__c: string | null;
  fw1__Billing_Zip__c: string;
  fw1__Cleared_For_Fulfillment__c: boolean;
  fw1__Connected_Account_Id__c: string | null;
  fw1__Contact_Name__c: string;
  fw1__Contact__c: string;
  fw1__Credit_Card_Failure_Reason__c: string | null;
  fw1__Current_Installment_Age__c: number;
  fw1__Delinquent__c: boolean;
  fw1__Description__c: string | null;
  fw1__Do_Not_Allow_Credit_Card_Payment_Online__c: boolean;
  fw1__Do_Not_Send_Receipt__c: boolean;
  fw1__Do_Not_Store_Credit_Card__c: boolean;
  fw1__Domain_Name__c: string;
  fw1__Due_Date__c: string;
  fw1__Due_Installment_Amount__c: number | null;
  fw1__Email_CC_Recipients__c: string;
  fw1__Email_Currency_Symbol__c: string;
  fw1__Email_Footer__c: string;
  fw1__Email_Message__c: string;
  fw1__Email_Sent__c: boolean;
  fw1__Email_Subject__c: string;
  fw1__Email_me_a_copy_of_invoice__c: boolean;
  fw1__Enter_Down_Payment_In__c: string | null;
  fw1__Entity__c: string;
  fw1__From_Email_Address_Id__c: string | null;
  fw1__Installment_Fee__c: number | null;
  fw1__Installments_Reconfiguration_Needed__c: boolean;
  fw1__Invoice_Date__c: string;
  fw1__Invoice_Discount_Amount__c: number;
  fw1__Invoice_Discount_Percentage__c: number | null;
  fw1__Is_Overdue__c: boolean;
  fw1__Is_Paid__c: boolean;
  fw1__Is_Partial__c: boolean;
  fw1__Is_Payment_Pending__c: boolean;
  fw1__Is_Shipping_Taxable__c: boolean;
  fw1__Is_Stop_Collection__c: boolean;
  fw1__Is_Voided__c: boolean;
  fw1__Net_Amount__c: number;
  fw1__Net_Taxable_Amount__c: number;
  fw1__Number_of_Retries__c: number;
  fw1__Opportunity__c: string;
  fw1__PO_Number__c: string | null;
  fw1__Payment_Plan__c: string | null;
  fw1__Payment_Profile__c: string | null;
  fw1__Payment_Receipt_Email_Message__c: string;
  fw1__Payment_Receipt_Email_Subject__c: string;
  fw1__Pending_Auto_Bill_Pay__c: boolean;
  fw1__Pending_Settlement_Amount__c: number | null;
  fw1__Printed_Date__c: string | null;
  fw1__Processor_Category__c: string;
  fw1__Recipient_Email__c: string;
  fw1__Recipient_Name__c: string;
  fw1__Recurring_Invoice__c: string;
  fw1__Reply_To_Email__c: string | null;
  fw1__Required_Down_Payment_Amount__c: number | null;
  fw1__Required_Down_Payment__c: number | null;
  fw1__Retry_Date__c: string | null;
  fw1__Revolving_Balance__c: number | null;
  fw1__Shipping_And_Handling__c: number | null;
  fw1__Shipping_City__c: string | null;
  fw1__Shipping_Country__c: string | null;
  fw1__Shipping_State__c: string | null;
  fw1__Shipping_Street__c: string | null;
  fw1__Shipping_Zip__c: string | null;
  fw1__Status_Sys__c: string | null;
  fw1__Status__c: string | null;
  fw1__Surcharge_When_Paying_By_Credit_Card__c: number | null;
  fw1__Tax_Amount_Override__c: number | null;
  fw1__Tax_Amount__c: number;
  fw1__Tax__c: string | null;
  fw1__Terms_Accepted__c: boolean;
  fw1__Terms_And_Conditions_Link__c: string;
  fw1__Terms_And_Conditions__c: string | null;
  fw1__Terms__c: string | null;
  fw1__Total_Additional_Charges__c: number | null;
  fw1__Total_Invoice_Amount__c: number;
  fw1__Total_Invoice_Discount_Amount__c: number;
  fw1__Total_Paid_Amount__c: number;
  fw1__Total_Taxable_Discount_Amount__c: number;
  fw1__Current_Installment_Due_Date__c: string | null;
  fw1__Installment_Count__c: number;
  fw1__Total_Installment_Amount__c: number;
  fw1__Total_Installment_Fees__c: number;
  fw1__Total_Lines_Amount__c: number;
  fw1__Total_Lines_Count__c: number;
  fw1__Total_Lines_Discount_Amount__c: number;
  fw1__Total_Lines_Discount_Percentage__c: number;
  fw1__Total_Lines_Discount_Quantity__c: number;
  fw1__Total_Lines_Taxable_Amount__c: number;
  fw1__Total_Non_Installment__c: number;
  fw2__Opportunity__c: string;
  fw2__Order__c: string | null;
  fw2__Quote__c: string | null;
  Billing_Days__c: number;
  Contact_First_Name__c: string;
  Daily_Rate__c: number;
  Is_Prorated__c: boolean;
  Members_Adult_Report__c: string;
  Members_Child_Report__c: string;
  Membership_Type__c: string;
  Prorated_Amount__c: number | null;
  Members_Adult__c: number;
  Members_Child__c: number;
  Opportunity_Type__c: string;
  fw1__Customer_Reference__c: string | null;
  fw1__Merchant_Reference__c: string | null;
  First_Name__c: string;
  Payment_Method__c: string | null;
  Renewal_Risk_Assessment__c: string | null;
  Members__c: number;
  Refund_Status__c: string | null;
  Status__c: string | null;
  Status_Detail__c: string | null;
  Sollis_Health_Location__c: string;
  Last_Payment_Date__c: string | null;
  Do_Not_Create_Memberships__c: boolean;
  Payment_Date__c: string;
  Is_Due_Date_and_Payment_Date_Different__c: boolean;
  Opportunity_Id__c: string;
  Type__c: string;
  fw1__Payment_Intent__c: string | null;
  Credit_Amount__c: number | null;
  Total_Paid_Amount__c: number;
  Renewal_Date__c: string;
  Entity_Category__c: string;
  fw1__Auto_BillPay_Date__c: string | null;
  fw1__Auto_BillPay_Options__c: string;
  fw1__Current_Installment_Auto_BillPay_Date__c: string | null;
  fw1__Statement_Descriptor__c: string | null;
  Do_Not_Sync_to_Sage_Intacct__c: boolean;
  Account_Record_Type__c: string;
  Amount_Summary__c: string;
  Is_Waived__c: boolean;
  Loss_Reason_Detail__c: string;
  Opportunity_Record_Type__c: string;
  Price_Book__c: string;
  Revenue_Type__c: string;
  Sollis_Health_Market__c: string | null;
  Status_Detail2__c: string;
  Members_Employee__c: number;
  Total_Invoice_Quantity__c: number;
  Last_Payment_Year__c: number | null;
  Record_Type_Name__c: string;
  Last_Payment_Method__c: string | null;
  Discount_Item_Id__c: string;
  Synced_to_Sage__c: boolean;
  Total_Processing_Fees__c: number | null;
  Received_Payment_Accounting__c: boolean;
  Unlock__c: boolean;
  Contact_eCW_Account__c: string | null;
  fw1__Do_Not_Send_Invoice__c: boolean;
  fw1__Error_Message__c: string | null;
  fw1__Error_Type__c: string | null;
  fw1__Locale__c: string | null;
  fw1__File_Attachment_Ids__c: string | null;
  Promotion__c: string;
  fw1__Latest_Invoice_PDF__c: string | null;
  Pricebook_Id__c: string;
  Is_Multi_year_Renewal__c: boolean;
  Create_Renewal_Opportunity__c: boolean;
  Online_Payments_Link__c: string;
  fw1__Do_not_Add_Late_Fee__c: boolean;
  fw1__Transfer_Amount__c: number;
  Projected_Refund_Amount__c: number;
  Owner__c: string | null;
  Total_Refunded_Amount__c: number | null;
  Total_Captured_Amount__c: number | null;
  Sent_Ecommerce_Email__c: boolean;
  Meta_API_Response__c: string | null;
  No_Amount__c: boolean;
  Is_Manually_Paid__c: boolean;
  Online_Authorization_Payments_Link__c: string;
  Is_Confirmed__c: boolean;
  Account_Record_Type_Text__c: string;
  Last_Payment_Made_By__c: string | null;
  Contains_pediatrics__c: number;
  Lead__c: string | null;
  LeadIsEComm__c: boolean;
  fw1__Dual_Pricing_Percentage_Fee__c: number | null;
  fw1__Enable_Dual_Pricing__c: boolean;
  fw1__Is_Cash_Payment__c: boolean;
  fw1__Net_Cash_Amount__c: number;
  fw1__Net_Taxable_Cash_Amount__c: number;
  fw1__Tax_Cash_Amount__c: number;
  fw1__Total_Invoice_Cash_Amount__c: number;
  fw1__Total_Invoice_Discount_Cash_Amount__c: number;
  fw1__Total_Taxable_Discount_Cash_Amount__c: number;
  fw1__Total_Lines_Cash_Amount__c: number;
  fw1__Total_Lines_Taxable_Cash_Amount__c: number;
  Invoice_Signed_Date__c: string | null;
};

export type SalesforceInvoiceLineItemAttributes = {
  type: string;
  url: string;
};

export type SalesforceInvoiceLineItem = {
  attributes: SalesforceInvoiceLineItemAttributes;
  Id: string;
  Name: string;
  fw1__Amount__c: number;
  fw1__Quantity__c: number;
};

export type SalesforceInvoiceLineItemsResponse = {
  totalSize: number;
  done: boolean;
  records: SalesforceInvoiceLineItem[];
};
